import { useCallback } from 'react';
import { useRouter } from 'next/router';

import { logger } from '@zep/core-logger';

import type { QuizSetDetailsRes } from '@/api/gen/_model/quizSetDetailsRes';
import { useUser } from '@/lib/auth';
import { setReferrer } from '@/shared/features/enter-referrer-tracking';
import { useShowSaveToFolderModal } from '@/shared/features/quiz-folder';

interface UseDuplicateHandlerParams {
  quizId: string;
  quizData: QuizSetDetailsRes;
}

/**
 * useDuplicateHandler - `내 퀴즈로 저장` 핸들러
 *
 * @description
 * - 비로그인 사용자: 로그인 페이지로 리다이렉트 (콜백 URL 포함)
 * - 로그인 사용자: **저장 위치 선택 모달**을 연다
 *
 * 빌드 페이지(`/duplicate/[id]`)를 거치지 않는다. 위치를 고르면 서버가 원본을 읽어
 * 복사본을 만들고 그 자리에 배정한다(`POST /quizsets/{id}/copy`). 프리미엄 맵·문항이
 * 섞여 있어도 잠긴 내 퀴즈로 저장되므로 잠김 여부로 흐름을 나누지 않는다.
 */
export const useDuplicateHandler = ({
  quizId,
  quizData,
}: UseDuplicateHandlerParams) => {
  const router = useRouter();
  const { user } = useUser();
  const showSaveToFolderModal = useShowSaveToFolderModal();

  return useCallback(() => {
    logger.track('enter_duplicate', {
      location: 'detail',
      spaceId: quizData.id,
      title: quizData.title,
    });
    setReferrer({
      from: '/quiz',
      location: 'quiz_detail_duplicate_button',
    });

    // 비로그인 상태면 로그인 페이지로 리다이렉트 (로그인 후 현재 페이지로 돌아옴)
    if (!user) {
      const currentUrl = `/quiz/${quizId}`;
      void router.push({
        pathname: '/login',
        query: { callbackUrl: currentUrl },
      });
      return;
    }

    void showSaveToFolderModal({
      id: quizData.id,
      /*
        이 CTA 는 게스트·뷰어의 `내 퀴즈로 저장` 과 **소유자의 `복사`** 를 모두 받는다.
        셋 다 하는 일은 같다 — 사본을 만든다. 소유자라고 이동으로 바뀌지 않는다.
      */
      intent: 'copy',
    });
  }, [router, user, quizId, quizData, showSaveToFolderModal]);
};
